import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { QAFormType, QualitySurveyRatingStarAppreciation } from "pages/project/common/taskBase.typing";
import { TASK_LIST_TYPE } from "pages/project/task/task.typing";

type QAFormRatingType = {
  title: string;
  titleId: number;
  details: string;
  ratings: Array<{ key: number; value: boolean }>;
};

export interface DeliverFormModel {
  comment?: string; // For QA/MT feedback
  remarks?: string; // For general remarks
  qaFormRatings: QAFormRatingType[];
}

function initForm({ ratings: fromRatings, titles }: QAFormType): QAFormRatingType[] {
  return titles.map(({ description: title, id: titleId, details: details }) => {
    const ratings = fromRatings.map(({ key }) => {
      return { key, value: false };
    });
    return { title, titleId, details, ratings };
  });
}

// Helper functions for form validation
const hasAnyRatingSelected = (qaFormRatings: QAFormRatingType[]) =>
  qaFormRatings.some(({ ratings }) => ratings.some(({ value }) => value));

const hasCommentFilled = (comment?: string) => comment && comment.trim().length > 0;

const getAnsweredQuestionsCount = (qaFormRatings: QAFormRatingType[]) =>
  qaFormRatings.filter(({ ratings }) => ratings.some(({ value }) => value)).length;

const areAllQuestionsAnswered = (qaFormRatings: QAFormRatingType[]) =>
  getAnsweredQuestionsCount(qaFormRatings) >= qaFormRatings.length;

const buildRatingsPayload = (qaFormRatings: QAFormRatingType[]) =>
  qaFormRatings.map(({ ratings, titleId }) => {
    const rating = ratings.find(({ value }) => value);
    return { key: titleId, value: rating?.key ?? null };
  });

export const COMMENT_MAX_CHAR_LIMIT = 1024;

type SubmitCallback = (data: {
  comment?: string;
  remarks?: string;
  ratings?: Array<{ key: number; value: number | null }>;
}) => void;

export const useDeliverTaskForm = (
  taskId: string,
  qaForm?: QAFormType,
  shouldDisplayQAForm?: boolean,
  catToolId?: number,
  isUserSupplier?: boolean,
  translationTaskMtEngineFormRequirement?: boolean,
  onSubmit?: SubmitCallback, // Callback to handle form submission
  isOptionalForm?: boolean, // If true, empty forms are allowed
  isFeedbackOnly?: boolean, // If true, empty forms are not allowed even when optional
  isStarRating?: boolean // If true, this is a star rating form
) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [validationError, setValidationError] = useState<string | null>(null);
  const { register, reset, handleSubmit, watch, setValue, formState } = useForm<DeliverFormModel>({
    defaultValues: { qaFormRatings: [] },
    mode: "onChange",
  });
  const qaFormRatings = watch("qaFormRatings");

  const rated3StarsOrLess = qaFormRatings.some((qaFormRating) => {
    const filteredRatings = qaFormRating.ratings.filter(
      (rating) => rating.key <= QualitySurveyRatingStarAppreciation.ThreeStars
    );
    return filteredRatings.length > 0 ? filteredRatings.some((rating) => rating.value) : undefined;
  });

  const isQAFormMandatory = !isOptionalForm;

  useEffect(() => {
    if (qaForm) {
      setValue("qaFormRatings", initForm(qaForm));
    }
  }, [qaForm, setValue]);

  const onReset = () => {
    reset();
    setValidationError(null);
    const currentType = params.get("type") || TASK_LIST_TYPE.ongoing;
    navigate(`/project/task/list/${currentType}/${taskId}?${params.toString()}`);
  };

  return {
    qaFormRatings,
    formState,
    rated3StarsOrLess,
    setValue,
    register,
    validationError,
    reset: onReset,
    deliver: handleSubmit(({ comment, remarks, qaFormRatings }) => {
      const hasNoFormToDisplay = !shouldDisplayQAForm && !translationTaskMtEngineFormRequirement;

      // No form required: submit with remarks only
      if (hasNoFormToDisplay) {
        onSubmit?.({ comment, remarks, ratings: undefined });
        return;
      }

      const hasAnyRating = hasAnyRatingSelected(qaFormRatings);
      const hasComment = hasCommentFilled(comment);
      const hasAnyFeedback = hasAnyRating || hasComment;
      const isOptionalOrMtForm = isOptionalForm || translationTaskMtEngineFormRequirement;
      const isMandatoryQAForm = shouldDisplayQAForm && isQAFormMandatory && !isOptionalOrMtForm;

      // MANDATORY QA form (non-star): all questions must be answered
      if (isMandatoryQAForm && !isStarRating && !areAllQuestionsAnswered(qaFormRatings)) {
        setValidationError("task.modal.error-qa-from");
        return;
      }

      // MANDATORY QA form (star rating): at least one rating required
      if (isMandatoryQAForm && isStarRating && !hasAnyRating) {
        setValidationError("task.modal.error-qa-from");
        return;
      }

      // OPTIONAL/MT forms: comment without rating is not allowed
      if (isOptionalOrMtForm && hasComment && !hasAnyRating) {
        setValidationError("task.modal.feedback.error-comment-without-form");
        return;
      }

      // FEEDBACK ONLY: empty submission not allowed
      if (isFeedbackOnly && !hasAnyFeedback) {
        setValidationError("task.modal.feedback.error-empty-form");
        return;
      }

      // OPTIONAL/MT forms: empty form = just deliver without data
      if (isOptionalOrMtForm && !hasAnyFeedback) {
        setValidationError(null);
        onSubmit?.({ comment: undefined, remarks: undefined, ratings: undefined });
        return;
      }

      // Valid submission with form data
      setValidationError(null);
      const ratings = hasAnyRating ? buildRatingsPayload(qaFormRatings) : undefined;
      onSubmit?.({ comment, remarks, ratings });
    }),
  };
};
